import ReactCursorPosition from 'react-cursor-position';
import {Box, Center, SimpleGrid, Text} from '@chakra-ui/react';
import React, {useEffect, useState, useCallback} from 'react';
import {useRecoilValue} from 'recoil';
import {teamOrder} from './Nav';
import _ from 'lodash';
import {AnimatePresence, motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import Container from "../../../components/Container";

const Wrapper = motion(Box);

const TeamList = ({data: TEAM, categoriesData}) => {
    const {location, team: filterTeam} = useRecoilValue(teamOrder);
    const [activePeople, setActivePeople] = useState(TEAM);

    const keys = categoriesData.map((c) => c.name)

    const activeFilter = keys[filterTeam]

    useEffect(() => {
        if (location && activeFilter) {
            const membersByFilter = _.filter(TEAM, {teams: [{name: activeFilter}]});
            const membersByLocation = _.filter(membersByFilter, {office: location});
            setActivePeople(membersByLocation)
        } else if (location) {
            const membersByLocation = _.filter(TEAM, {office: location});
            setActivePeople(membersByLocation)
        } else if (activeFilter) {
            const membersByFilter = _.filter(TEAM, {teams: [{name: activeFilter}]});
            setActivePeople(membersByFilter)
        } else {
            setActivePeople(TEAM)
        }
    }, [location, activeFilter, TEAM])

    const teamsSlug = React.useMemo(
        () => _.flattenDeep(activePeople?.map((item) => item.teams)),
        [activePeople],
    );

    const allTeams = React.useMemo(() => {
        return _.uniqBy(teamsSlug, 'slug').reduce((acc, item) => {
            acc[item.slug] = {
                name: item.section,
                slug: item.slug,
                data: [],
            };
            return acc;
        }, {});
    }, [teamsSlug]);

    const teams = React.useMemo(() => {
        return activePeople?.reduce((acc, item) => {
            const ts = item.teams.find((fid) => {
                return !!acc[fid.slug];
            });
            if (!!ts) {
                // console.log(ts);
                const selectedTeam = acc[ts.slug];
                acc[ts.slug] = {
                    name: selectedTeam.name,
                    slug: selectedTeam.slug,
                    data: [...selectedTeam.data, item],
                };
            }
            return acc;
        }, allTeams);
    }, [allTeams, activePeople]);

    const teamsDisplay = React.useMemo(() => {
        let renderedTeams = {}
        Object.keys(teams).map((key) => {
            if (!renderedTeams[teams[key].name]) {
                renderedTeams[teams[key].name] = {name: teams[key].name, data: teams[key].data}
            } else {
                renderedTeams[teams[key].name].data.push(...teams[key].data)
            }
        })

        return renderedTeams
    }, [teams]);


    const team = useCallback((item) => {
        const hasPerson = item.data.filter((fid) => fid.office === location);
        if (location && hasPerson.length === 0) {
            return null;
        }
        return (
            <Box px={3}>
                <Wrapper
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    transition={{duration: 0.4}}
                    display={'flex'}
                    flexDirection={'column'}
                    textAlign={'left'}
                >
                    <Text fontSize={'2rem'} letterSpacing={'-1px'} mb={'10'}>
                        {item.name}
                    </Text>
                    <SimpleGrid columns={[2, 2, 2, 3, 4]} spacingY="60px" spacingX="20px" mb={'80px'}>
                        {item.data.sort((a, b) => a.last_name.localeCompare(b.last_name)).map((person, index) => {
                            const filterBaseOnLocation = person.office === location;
                            const node = (
                                <Box key={index}>
                                    <TeamItem
                                        slug={person.slug}
                                        src={person.user_image}
                                        name={person.full_name}
                                        as={person.job_title}
                                        office={person?.office}
                                        focus={person?.focus}
                                    />
                                </Box>
                            );
                            if (location && filterBaseOnLocation) {
                                return node;
                            }
                            if (!location) {
                                return node;
                            }
                            return null;
                        })}
                    </SimpleGrid>
                </Wrapper>
            </Box>
        );
    }, [location]);
    const renderTeams = React.useMemo(() => {
        if (!teamsDisplay) return null;
        return Object.keys(teamsDisplay).map((key) => {
            return <React.Fragment key={key}>{team(teamsDisplay[key])}</React.Fragment>;
        });
    }, [teamsDisplay, team]);

    const renderTeamsFiltered = React.useMemo(() => {
        if (!teams) return null;
        return Object.keys(teams).map((key) => {
            return <React.Fragment key={key}>{team(teams[key])}</React.Fragment>;
        });
    }, [teams, team]);

    const display = React.useMemo(
        () => (filterTeam === null ? renderTeams : renderTeamsFiltered[filterTeam]),
        [filterTeam, renderTeams, renderTeamsFiltered],
    )

    let section = teamsSlug[Object.keys(teamsSlug)[0]]

    if (section && activePeople.length > 0) {
        section = {
            ...section,
            data: [...activePeople]
        }
    }

    if (section?.data && (location || activeFilter)) {
        return (
            <Box borderX={'1px'} borderColor={'brandAlpha.200'} px={3}>
                <Container>
                    <Wrapper
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={{duration: 0.4}}
                        display={'flex'}
                        flexDirection={'column'}
                        textAlign={'left'}
                    >
                        <Text fontSize={'2rem'} letterSpacing={'-1px'} mb={'10'}>
                            {section?.section}
                        </Text>
                        <SimpleGrid columns={[2, 2, 2, 3, 4]} spacingY="60px" spacingX="20px" mb={'80px'}>
                            {section?.data?.sort((a, b) => a.last_name.localeCompare(b.last_name))?.map((person, index) => {
                                return (
                                    <Box key={index}>
                                        <TeamItem
                                            slug={person.slug}
                                            src={person.user_image}
                                            name={person.full_name}
                                            as={person.job_title}
                                            office={person?.office}
                                            focus={person?.focus}
                                        />
                                    </Box>
                                )
                            })}
                        </SimpleGrid>
                    </Wrapper>
                </Container>
            </Box>
        );
    }

    return (
        <>
            <Box borderX={'1px'} borderColor={'brandAlpha.200'} as="section">
                <Box px={'1rem'}>
                    <Container>
                        <AnimatePresence>
                            {display}
                        </AnimatePresence>
                    </Container>
                </Box>
            </Box>
        </>
    );
};

export default TeamList;

const TeamItem = (props) => {
    const {src, name, as, slug, office, focus} = props;
    const [{x, y}, setMouseCoordinates] = useState({x: 0, y: 0});
    return (
        <Box as={Link} to={`/teammember/${slug}`}>
            <Box position={'relative'}>
                <ReactCursorPosition
                    onPositionChanged={({position: {x, y}}) => {
                        setMouseCoordinates({x, y});
                    }}
                >
                    <Box
                        _hover={{
                            '& #overlay': {
                                opacity: 1,
                                transition: '150ms all ease',
                            },
                            '&:before': {
                                content: "''",
                                position: 'absolute',
                                width: '100%',
                                left: 0,
                                height: '1px',
                                backgroundColor: 'white',
                                opacity: 0.2,
                                top: y,
                            },
                            '&:after': {
                                content: "''",
                                position: 'absolute',
                                height: '100%',
                                top: 0,
                                width: '1px',
                                backgroundColor: 'white',
                                opacity: 0.2,
                                left: x,
                            },
                        }}
                        userSelect={'none'}
                        position={'relative'}
                    >
                        {x > 70 && y > 40 && y < 350 && (
                            <Text
                                fontSize={'sm'}
                                letterSpacing={'-1px'}
                                lineHeight={'14px'}
                                as={'span'}
                                position={'absolute'}
                                top={y + 7}
                                left={x - 70}
                                id={'overlay'}
                                opacity={0}
                                color={'white'}
                                userSelect={'none'}
                            >
                                Read more
                            </Text>
                        )}
                        <Center
                            sx={{
                                backgroundImage: `url(${src})`,
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                backgroundSize: 'cover',
                            }}
                            paddingBottom={'119%'}
                            mb={'0.625rem'}
                            width={'100%'}
                        ></Center>
                        <Box
                            id={'overlay'}
                            position={'absolute'}
                            inset={0}
                            px={'20px'}
                            py={'15px'}
                            bgColor={'brandAlpha.600'}
                            opacity={0}
                        >
                            {focus && (
                                <Text
                                    mb={'2px'}
                                    fontSize={'14px'}
                                    lineHeight={'3xl'}
                                    textTransform={'capitalize'}
                                >
                                    <Text as={'span'} textColor={'whiteAlpha.600'}>
                                        Focus Areas:
                                    </Text>{' '}
                                    <Text as={'span'} textColor={'white'}>
                                        {focus}
                                    </Text>
                                </Text>
                            )}
                            {office && (
                                <Text
                                    mb={'2px'}
                                    fontSize={'14px'}
                                    lineHeight={'3xl'}
                                    textTransform={'capitalize'}
                                >
                                    <Text as={'span'} textColor={'whiteAlpha.600'}>
                                        Location:
                                    </Text>{' '}
                                    <Text as={'span'} textColor={'white'}>
                                        {office}
                                    </Text>
                                </Text>
                            )}
                        </Box>
                    </Box>
                </ReactCursorPosition>
            </Box>

            <Text
                textTransform={'capitalize'}
                fontWeight={'semibold'}
                fontSize={'md'}
                lineHeight={'8'}
                textAlign={'center'}
            >
                {name}
            </Text>
            <Text
                textTransform={'uppercase'}
                fontWeight={'semibold'}
                fontSize={'xs'}
                lineHeight={'4'}
                textAlign={'center'}
                textColor="#7A7B7B"
            >
                {as}
            </Text>
        </Box>
    );
};
